import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SidebarLayout from "./SidebarLayout";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

// Common US timezones for quick selection
const COMMON_TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Phoenix", label: "Arizona (No DST)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HST)" },
];

// All IANA timezones (for advanced users)
const ALL_TIMEZONES = [
  ...COMMON_TIMEZONES,
  { value: "America/Puerto_Rico", label: "Puerto Rico (AST)" },
  { value: "America/Toronto", label: "Toronto, Canada (ET)" },
  { value: "America/Vancouver", label: "Vancouver, Canada (PT)" },
  { value: "Europe/London", label: "London, UK (GMT/BST)" },
  { value: "Europe/Paris", label: "Paris, France (CET/CEST)" },
  { value: "Asia/Tokyo", label: "Tokyo, Japan (JST)" },
  { value: "Asia/Shanghai", label: "Shanghai, China (CST)" },
  { value: "Australia/Sydney", label: "Sydney, Australia (AEDT/AEST)" },
];

const LocationTimezone = () => {
  const navigate = useNavigate();
  const { locationId } = useParams();
  const [location, setLocation] = useState(null);
  const [timezone, setTimezone] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAllTimezones, setShowAllTimezones] = useState(false);

  const fetchLocation = React.useCallback(async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await axios.get(
        `${API_BASE_URL}/api/shops/${locationId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setLocation(response.data);
      setTimezone(response.data.timezone || "");
      setLoading(false);
    } catch (error) {
      alert("Failed to load location");
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    fetchLocation();
  }, [fetchLocation]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("ogestyle_token");

      await axios.put(
        `${API_BASE_URL}/api/shops/${locationId}`,
        { timezone },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      alert("Timezone updated successfully!");
      navigate("/dashboard/locations");
    } catch (error) {
      alert("Failed to save timezone");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="locations-page" style={{ padding: "40px", textAlign: "center" }}>
          <p>Loading location...</p>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div
        className="locations-page"
        style={{ padding: "24px", maxWidth: "800px", margin: "0 auto" }}
      >
        {/* Header */}
        <div style={{ marginBottom: "32px" }}>
          <button
            onClick={() => navigate("/dashboard/locations")}
            style={{
              background: "none",
              border: "none",
              color: "#3b82f6",
              cursor: "pointer",
              fontSize: "14px",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            ← Back to Locations
          </button>
          <h1
            style={{ fontSize: "28px", fontWeight: "700", margin: "0 0 8px 0" }}
          >
            Timezone Settings
          </h1>
          <p style={{ fontSize: "14px", color: "#666", margin: 0 }}>
            Set the timezone for {location?.name}
          </p>
        </div>

        {/* Current Timezone Display */}
        <div
          style={{
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "24px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "8px",
            }}
          >
            <span style={{ fontSize: "20px" }}>🌍</span>
            <strong style={{ fontSize: "15px", color: "#1e40af" }}>
              Current Timezone
            </strong>
          </div>
          <p
            style={{
              fontSize: "14px",
              color: "#1e3a8a",
              margin: "0 0 4px 28px",
            }}
          >
            {COMMON_TIMEZONES.find((tz) => tz.value === timezone)?.label ||
              timezone}
          </p>
          <p
            style={{ fontSize: "12px", color: "#64748b", margin: "0 0 0 28px" }}
          >
            Current time:{" "}
            {new Date().toLocaleString("en-US", {
              timeZone: timezone,
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              timeZoneName: "short",
            })}
          </p>
        </div>

        {/* Timezone Selector */}
        <div
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "24px",
          }}
        >
          <h2
            style={{
              fontSize: "18px",
              fontWeight: "600",
              marginBottom: "16px",
            }}
          >
            Select Timezone
          </h2>

          {/* Common Timezones */}
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Common US Timezones
            </label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            >
              <option value="">Select your timezone...</option>
              {COMMON_TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>

          {/* Show All Timezones Toggle */}
          <button
            onClick={() => setShowAllTimezones(!showAllTimezones)}
            style={{
              background: "none",
              border: "none",
              color: "#3b82f6",
              cursor: "pointer",
              fontSize: "13px",
              marginBottom: "16px",
            }}
          >
            {showAllTimezones ? "▼" : "▶"} {showAllTimezones ? "Hide" : "Show"}{" "}
            International Timezones
          </button>

          {/* All Timezones (Expanded) */}
          {showAllTimezones && (
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                All Timezones
              </label>
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                size={10}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                <option value="">Select your timezone...</option>
                {ALL_TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Info Box */}
          <div
            style={{
              background: "#fef3c7",
              border: "1px solid #fcd34d",
              borderRadius: "8px",
              padding: "12px",
              marginBottom: "24px",
            }}
          >
            <p style={{ fontSize: "13px", color: "#78350f", margin: 0 }}>
              <strong>⚠️ Important:</strong> This timezone affects:
            </p>
            <ul
              style={{
                fontSize: "13px",
                color: "#78350f",
                margin: "8px 0 0 20px",
                paddingLeft: "0",
              }}
            >
              <li>Booking availability calculations</li>
              <li>Business hours display</li>
              <li>Appointment confirmations to customers</li>
              <li>Reports and analytics time grouping</li>
            </ul>
          </div>

          {/* Save Button */}
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              onClick={() => navigate("/dashboard/locations")}
              style={{
                padding: "12px 24px",
                background: "white",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "15px",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: "12px 24px",
                background: saving ? "#9ca3af" : "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: saving ? "not-allowed" : "pointer",
                fontSize: "15px",
                fontWeight: "600",
              }}
            >
              {saving ? "Saving..." : "Save Timezone"}
            </button>
          </div>
        </div>
      </div>
    </SidebarLayout>
  );
};

export default LocationTimezone;
